import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Typography
} from '@mui/material';
import { changePassword, rawApiClient } from '../services/api';
import TwoStepManage from './auth/TwoStepManage';

const ChangePasswordDialog = ({ open, onClose, token, forcedChange = false, onOpenMfa }) => {
  // Two-step sign-in status (security batch 4), shown under the password form.
  const [mfa, setMfa] = useState(null);
  const [mfaRefresh, setMfaRefresh] = useState(0);
  useEffect(() => {
    if (!open || forcedChange) return;
    let cancelled = false;
    rawApiClient.get('/auth/mfa/status').then(r => { if (!cancelled) setMfa(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [open, forcedChange, mfaRefresh]);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    
    // Validate inputs
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    // Validate password complexity
    const validatePassword = (password) => {
      // Check for minimum length of 8 characters
      if (password.length < 8) {
        return { isValid: false, message: 'Password must be at least 8 characters long' };
      }

      // Check for uppercase letter
      if (!/[A-Z]/.test(password)) {
        return { isValid: false, message: 'Password must contain at least one uppercase letter' };
      }

      // Check for lowercase letter
      if (!/[a-z]/.test(password)) {
        return { isValid: false, message: 'Password must contain at least one lowercase letter' };
      }

      // Check for number
      if (!/[0-9]/.test(password)) {
        return { isValid: false, message: 'Password must contain at least one number' };
      }

      // Check for special character
      if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
        return { isValid: false, message: 'Password must contain at least one special character' };
      }

      return { isValid: true };
    };
    
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.message);
      return;
    }
    
    setLoading(true);
    
    try {
      await changePassword({ currentPassword, newPassword }, token);
      setSuccess(true);
      // Reset form after successful submission
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Close the dialog after a delay
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // If this is a forced password change, don't allow closing without changing
    if (forcedChange && !success) {
      setError('You must change your password to proceed.');
      return;
    }
    
    if (!loading) {
      // Reset form state
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown={forcedChange}
    >
      <DialogTitle>{forcedChange ? 'Password Change Required' : 'Change Password'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {forcedChange && !success && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              You must change your password now to proceed. This is required by the system administrator.
            </Alert>
          )}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>Password changed successfully!</Alert>}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Current Password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              fullWidth
              variant="outlined"
              autoComplete="current-password"
              disabled={loading}
              required
            />
            
            <TextField
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              variant="outlined"
              autoComplete="new-password"
              disabled={loading}
              required
              helperText="Password must be at least 8 characters long with 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character"
            />
            
            <TextField
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              variant="outlined"
              autoComplete="new-password"
              disabled={loading}
              required
            />
          </Box>
          {!forcedChange && mfa && onOpenMfa && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2">Two-step sign-in</Typography>
              {mfa.enabled ? (
                <TwoStepManage status={mfa} onChanged={() => setMfaRefresh(n => n + 1)} />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary">
                    Off. Add a code from your phone to every sign-in{mfa.required ? ' (required for your role)' : ''}.
                  </Typography>
                  <Button size="small" variant="outlined" onClick={onOpenMfa}>Set up</Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleClose} 
            disabled={loading || forcedChange}
            sx={{ display: forcedChange ? 'none' : 'inline-flex' }}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Changing Password...' : 'Change Password'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ChangePasswordDialog;
